import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Mail, MapPin, Phone } from "lucide-react";
import { COMPANY, NAV, SERVICES } from "../lib/siteData";
import { Logo } from "./Logo";

const TikTokIcon = ({ size = 18 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
    >
        <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
    </svg>
);

export const Footer = () => {
    return (
        <footer
            className="bg-hhc-ink text-hhc-cream mt-32 relative overflow-hidden"
            data-testid="site-footer"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 pt-24 pb-12">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-14">
                    <div className="md:col-span-5">
                        <div className="bg-hhc-sun rounded-2xl p-6 inline-block">
                            <Logo />
                        </div>
                        <p className="mt-8 font-display text-3xl md:text-4xl leading-[1.1] text-hhc-ivory max-w-md">
                            Transforming spaces, creating homes across Kuala Lumpur & Selangor.
                        </p>
                        <p className="mt-6 text-hhc-cream/70 text-sm max-w-md leading-relaxed">
                            {COMPANY.legalName} · {COMPANY.ssm}
                        </p>
                    </div>

                    <div className="md:col-span-3">
                        <div className="text-[11px] tracking-[0.28em] uppercase text-hhc-clay mb-6">
                            Explore
                        </div>
                        <ul className="space-y-3">
                            {NAV.map((n) => (
                                <li key={n.to}>
                                    <Link
                                        to={n.to}
                                        className="text-hhc-cream/80 hover:text-hhc-sun transition-colors duration-300 text-[15px]"
                                        data-testid={`footer-${n.testId}`}
                                    >
                                        {n.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>

                        <div className="text-[11px] tracking-[0.28em] uppercase text-hhc-clay mt-10 mb-6">
                            Services
                        </div>
                        <ul className="space-y-3">
                            {SERVICES.slice(0, 4).map((s) => (
                                <li key={s.slug}>
                                    <Link
                                        to={`/services#${s.slug}`}
                                        className="text-hhc-cream/70 hover:text-hhc-sun transition-colors duration-300 text-[14px]"
                                    >
                                        {s.title}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="md:col-span-4">
                        <div className="text-[11px] tracking-[0.28em] uppercase text-hhc-clay mb-6">
                            Studio
                        </div>
                        <ul className="space-y-5 text-[14px] text-hhc-cream/85 leading-relaxed">
                            <li className="flex gap-3">
                                <MapPin size={18} className="mt-0.5 text-hhc-sun shrink-0" />
                                <span>{COMPANY.address}</span>
                            </li>
                            <li className="flex gap-3 items-center">
                                <Phone size={18} className="text-hhc-sun shrink-0" />
                                <a
                                    href={`tel:+${COMPANY.phoneRaw}`}
                                    className="hover:text-hhc-sun transition-colors"
                                    data-testid="footer-phone"
                                >
                                    {COMPANY.phone}
                                </a>
                            </li>
                            <li className="flex gap-3 items-center">
                                <Mail size={18} className="text-hhc-sun shrink-0" />
                                <a
                                    href={`mailto:${COMPANY.email}`}
                                    className="hover:text-hhc-sun transition-colors break-all"
                                    data-testid="footer-email"
                                >
                                    {COMPANY.email}
                                </a>
                            </li>
                        </ul>

                        <div className="flex items-center gap-3 mt-8">
                            <a
                                href={COMPANY.instagram}
                                target="_blank"
                                rel="noreferrer"
                                className="w-11 h-11 grid place-items-center rounded-full border border-hhc-cream/25 hover:bg-hhc-sun hover:text-hhc-ink hover:border-hhc-sun transition-colors"
                                aria-label="Instagram"
                                data-testid="footer-instagram"
                            >
                                <Instagram size={17} strokeWidth={1.6} />
                            </a>
                            <a
                                href={COMPANY.tiktok}
                                target="_blank"
                                rel="noreferrer"
                                className="w-11 h-11 grid place-items-center rounded-full border border-hhc-cream/25 hover:bg-hhc-sun hover:text-hhc-ink hover:border-hhc-sun transition-colors"
                                aria-label="TikTok"
                                data-testid="footer-tiktok"
                            >
                                <TikTokIcon />
                            </a>
                            <a
                                href={COMPANY.whatsapp}
                                target="_blank"
                                rel="noreferrer"
                                className="w-11 h-11 grid place-items-center rounded-full border border-hhc-cream/25 hover:bg-hhc-sun hover:text-hhc-ink hover:border-hhc-sun transition-colors"
                                aria-label="WhatsApp"
                                data-testid="footer-whatsapp"
                            >
                                <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                                    <path d="M20.5 3.5A11 11 0 0 0 3.6 17.9L2 22l4.2-1.6A11 11 0 1 0 20.5 3.5Zm-8.5 18a9.5 9.5 0 0 1-4.8-1.3l-.3-.2-2.5.9.8-2.4-.2-.3A9.5 9.5 0 1 1 12 21.5Zm5.4-6.9c-.3-.2-1.7-.9-2-1s-.5-.2-.7.2-.8 1-1 1.2-.4.2-.7 0a7.7 7.7 0 0 1-2.3-1.4 8.5 8.5 0 0 1-1.6-2c-.2-.3 0-.5.1-.7l.4-.5c.2-.2.2-.3.3-.5s0-.4 0-.5-.7-1.6-.9-2.2-.5-.5-.7-.5H8c-.2 0-.5 0-.8.3a3 3 0 0 0-1 2.4c0 1.4 1 2.8 1.2 3s2.1 3.2 5.2 4.5c.7.3 1.3.5 1.8.6.8.2 1.4.2 2 .1.6-.1 1.7-.7 2-1.4s.3-1.3.2-1.4-.3-.2-.6-.3Z"/>
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>

                <div className="mt-20 pt-8 border-t border-hhc-cream/15 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <p className="text-[12px] text-hhc-cream/50 tracking-wide">
                        © {new Date().getFullYear()} {COMPANY.legalName}. All rights reserved.
                    </p>
                    <p className="text-[12px] text-hhc-cream/50 tracking-wide">
                        Serving {COMPANY.serviceArea}
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
